import { Injectable } from '@angular/core';

import { AppConstant } from '../utilities/app.constant';
import { AppHelper } from '../utilities/app.helper';

export type RecentAlertTab = 'action' | 'unread' | 'completed' | 'all';

export interface RecentAlertTabDef {
  key: RecentAlertTab;
  label: string;
}

@Injectable({ providedIn: 'root' })
export class InterventionFilterService {
  /** Tab definitions shown in the redesigned Recent Alerts header. */
  readonly tabs: RecentAlertTabDef[] = [
    { key: 'action', label: 'ACTION REQUIRED' },
    { key: 'unread', label: 'UNREAD' },
    { key: 'completed', label: 'COMPLETED' },
    { key: 'all', label: 'ALL' },
  ];

  isUnread(log: any): boolean {
    if (!log) {
      return false;
    }

    const readStatus = String(log.readStatus ?? log.read_status ?? '')
      .trim()
      .toLowerCase();

    return readStatus === 'unread';
  }

  private getStateCode(log: any): string {
    return String(log?.stateCode ?? log?.state_code ?? '').trim();
  }

  private getTimeExpire(log: any): any {
    return log?.timeExpire ?? log?.time_expire ?? null;
  }

  private isExpired(log: any): boolean {
    const expiry = this.getTimeExpire(log);
    if (!expiry) {
      return false;
    }

    return new Date(expiry).getTime() <= Date.now();
  }

  isCompleted(log: any): boolean {
    if (!log) {
      return false;
    }

    if (log.isComplete === true || log.is_complete === true) {
      return true;
    }

    const stateCode = this.getStateCode(log);
    return (
      stateCode === AppConstant.INTERVENTION_STATE_CODE.INITIATE_COMPLETED ||
      stateCode ===
        AppConstant.INTERVENTION_STATE_CODE.RESOLUTION_ACTION_COMPLETED
    );
  }

  private asBoolean(value: unknown): boolean {
    if (typeof value === 'string') {
      return value.trim().toLowerCase() === 'true';
    }

    return Boolean(value);
  }

  isActionRequired(log: any): boolean {
    if (!log || this.isCompleted(log)) {
      return false;
    }

    const stateCode = this.getStateCode(log);

    if (
      stateCode === AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_DRAFT
    ) {
      return false;
    }

    if (stateCode === AppConstant.INTERVENTION_STATE_CODE.INITIATE_DRAFT) {
      return false;
    }

    if (stateCode === AppConstant.INTERVENTION_STATE_CODE.RESOLUTION_REQUIRED) {
      return true;
    }

    if (
      stateCode ===
      AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_COMPLETED
    ) {
      return (
        this.isExpired(log) ||
        this.asBoolean(log?.isRequiredFutherAction) ||
        this.asBoolean(log?.is_required_futher_action)
      );
    }

    if (
      stateCode === AppConstant.INTERVENTION_STATE_CODE.RESOLUTION_ACTION_DRAFT
    ) {
      return false;
    }

    return (
      this.asBoolean(log?.isRequiredFutherAction) ||
      this.asBoolean(log?.is_required_futher_action)
    );
  }

  isDrafted(log: any): boolean {
    if (!log || this.isCompleted(log)) {
      return false;
    }

    const stateCode = this.getStateCode(log);
    if (
      stateCode === AppConstant.INTERVENTION_STATE_CODE.INITIATE_DRAFT ||
      stateCode === AppConstant.INTERVENTION_STATE_CODE.INITIATE_ACTION_DRAFT
    ) {
      return true;
    }

    if (
      stateCode ===
        AppConstant.INTERVENTION_STATE_CODE.RESOLUTION_ACTION_DRAFT &&
      !this.isExpired(log)
    ) {
      return true;
    }

    return (
      String(log?.status ?? '').trim() === 'INITIATE' &&
      (log?.hasSent === false || log?.has_sent === false)
    );
  }

  countNew(logs: any[]): number {
    return (logs || []).filter(log => this.isUnread(log)).length;
  }

  private matchesKeyword(log: any, keyword: string): boolean {
    const term = (keyword || '').trim().toLowerCase();
    if (!term) {
      return true;
    }
    const haystack = [
      log?.wellName,
      log?.wellboreName,
      log?.rigName,
      log?.projectName,
      log?.operatorName,
      log?.region,
      log?.country,
      log?.description,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(term);
  }

  getStatusTone(log: any): 'green' | 'yellow' | 'red' | '' {
    if (!log) {
      return '';
    }

    const stateCode = this.getStateCode(log);
    const tone = AppHelper.MathFunctions.getColorOfInterventionStatus(
      stateCode,
      this.getTimeExpire(log)
    );

    return tone.replace('-tag', '') as 'green' | 'yellow' | 'red' | '';
  }

  /**
   * Applies the keyword filter only. `logs` is already scoped to the active
   * tab by the backend (`recentAlertTab` query param), so re-filtering by tab
   * here would hide rows the backend included.
   */
  apply(logs: any[], _tab: RecentAlertTab, keyword: string): any[] {
    return (logs || []).filter(log => this.matchesKeyword(log, keyword));
  }
}
